<template>
	<div
		:class="[
			'textarea',
			...variantClassList,
			hasError && 'has-error',
			sizeIconErrorFeature && 'has-icon-error-feature',
			disabled && 'is-disabled'
		]"
		:disabled="disabled"
	>
		<Label
			class="textarea__label"
			v-if="labelText"
			:labelText="labelText"
			:forElementId="uid"
		>
			<template v-slot:leading>
				<slot name="beforeLabel"> </slot>
			</template>

			<template v-slot:trailing>
				<slot name="afterLabel"> </slot>
			</template>
		</Label>

		<div class="textarea__body" :style="[textAreaWidthStyle]">
			<div class="textarea__field-container">
				<textarea
					class="textarea__field"
					:placeholder="placeholder"
					:aria-label="ariaLabel"
					:rows="rows"
					@input="inputSearchText"
					@change="updateSearchText"
					@blur="blurSearchText"
					@keydown.tab="withFocus = true"
					@keyup="onKeyUp"
					@click="onClick"
					@focus="onInputFocus"
					v-model="currentValue"
					ref="input"
				></textarea>
			</div>
			<span class="textarea__word-count" v-if="hasCounter">
				{{ (currentValue && currentValue.length) || 0 }}/{{ maxLength }}
			</span>
		</div>

		<HelpFeedback
			:style="[textAreaWidthStyle]"
			class="textarea__help-feedback"
			:helpText="helpText"
			v-if="helpText"
		/>
		<ErrorFeedback
			class="textarea__error-feedback"
			:errorMessages="errorMessages"
			v-if="hasError"
		/>
	</div>
</template>

<script>
import Label from '@/components/forms/shared-stateless-elements/label.vue';
import ErrorFeedback from '@/components/forms/shared-stateless-elements/error-feedback';
import HelpFeedback from '@/components/forms/shared-stateless-elements/help-feedback';
import formComponentMixin from '@/mixins/form-component-mixin';
import componentVariantMixin from '@/mixins/component-variant-mixin';
import baseComponentMixin from '@/mixins/base-component-mixin';
import variantClasses from './variant-classes/textarea-variant-classes';
import baseInputMixin from '@/mixins/base-input-mixin';

export default {
	name: 'TextareaGroup',
	mixins: [
		formComponentMixin,
		componentVariantMixin,
		baseComponentMixin,
		baseInputMixin
	],
	components: {
		Label,
		HelpFeedback,
		ErrorFeedback
	},
	created() {
		this.variantClasses = variantClasses;
	},
	props: {
		helpText: {
			type: String,
			default: null
		},
		ariaLabel: {
			type: String,
			default: ''
		},
		labelText: {
			type: String,
			default: ''
		},
		width: {
			type: [String, Number],
			default: null
		},
		height: {
			type: [String, Number],
			default: null
		},
		placeholder: {
			type: String,
			default: ''
		},
		hasCounter: {
			type: Boolean,
			default: false
		},
		/*errorMessages: {
			type: Array,
			default: () => []
		},*/
		rows: {
			type: Number,
			default: 4
		},
		sizeIconErrorFeature: {
			type: Boolean,
			default: false
		},
		maxLength: {
			type: Number,
			default: 200
		}
	},
	computed: {
		hasError() {
			return this.errorMessages && this.errorMessages.length > 0;
		},
		textAreaWidthStyle() {
			if (typeof this.width === 'number') {
				return {
					width: `${this.width}px`
				};
			} else {
				return {
					width: this.width
				};
			}
		}
	},
	methods: {
		onInput() {}
	}
};
</script>

<style lang="scss" scoped>
@include scrollbar('borderless');
.textarea {
	display: block;
	font-size: $field-font-size;
	text-align: left;
	color: var(--field-fg);

	&__label {
		display: flex;
		margin-bottom: $gutter-sm;
	}

	&__body {
		position: relative;
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
	}

	&__field-container {
		position: relative;
		display: flex;
		align-items: center;

		&::after {
			content: none;
			@include transform-vertical-center;
			left: 0;
			transform: translateY(-50%);
			width: calc(100% + 1px);
			height: calc(100% + 1px);
			border-radius: $border-radius;
			border: $focus-width $focus-style var(--brand-2);
			pointer-events: none;
			z-index: $z-level1-1;
		}

		&:focus-within,
		&:hover {
			&::after {
				content: '';
				z-index: $z-level1-1;
			}

			.input__field {
				border-color: transparent;
			}

			& .input__clear-btn {
				display: inline-flex;
			}
		}
	}

	&__field {
		width: 100%;
		height: auto;
		resize: none;
		padding: $padding-sm;
		border: $field-border;
		border-radius: $border-radius;
		background: var(--field-bg);
		color: var(--field-fg);

		&:focus,
		&:active {
			outline: none;
		}
	}

	&__word-count {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		font-weight: $heading-font-weight-lg;
		margin-top: $gutter-sm;
		font-size: $txt-supporting-font-size;
		color: var(--neutral-4);
	}

	&.has-error {
		.textarea__field-container::after {
			content: '';
			border-color: var(--ui-negative);
		}
	}

	&__help-feedback {
		display: block;
		padding-right: $padding-xl;
		margin-top: -14px;
	}
}
@include left-label('textarea');

.textarea--left-label {
	display: grid;
	align-items: flex-start;
}
</style>
